"use client";

/* Il percorso del dossier principale (§ 6 ter, punto 4): una linea con un
   rombo per tappa e, sotto, la data breve. Passando sopra un rombo, o col
   tasto Tab, il rombo si ingrandisce e diventa rame, la linea si colora di
   rame dal primo fino a lui, e sopra compare la scheda della tappa (data in
   mono rame, titolo, «Apri ↗»). Il clic apre il dispaccio della tappa.

   Ogni tappa, anche sui dossier riservati, ha data, titolo e
   collegamento alla sua versione (chiarito il 20/10/2026: le soglie dei
   casi e dei report dicono come si forma un filone, non che cosa si vede).

   Client per un motivo solo: quale rombo è acceso. Il modello lo fa con
   nove regole `:has(li:nth-child(k):hover)` scritte per nove tappe; qui le
   tappe vanno da due a trenta, quindi è uno stato. Di serie nessuno è
   acceso, quindi server e idratazione coincidono. */

import Link from "next/link";
import { useState } from "react";

import type { TappaPercorso } from "@/lib/paese-dati";

/** Con più tappe di così le date brevi si accavallano: restano la prima e
 *  l'ultima, e le altre le dice la scheda. */
const DATE_SOTTO_TUTTE = 10;

export function PercorsoDossier({ tappe }: { tappe: TappaPercorso[] }) {
  const [acceso, setAcceso] = useState<number | null>(null);
  const n = tappe.length;
  if (n < 2) return null;
  const quota = acceso === null ? 0 : acceso / (n - 1);
  return (
    <ol
      className="relative m-0 mt-2 flex list-none justify-between p-0 pt-0"
      onMouseLeave={() => setAcceso(null)}
    >
      <span aria-hidden className="absolute top-[14px] right-[14px] left-[14px] h-[1.5px] bg-mare opacity-60" />
      <span
        aria-hidden
        className="absolute top-[13.5px] left-[14px] h-[2.5px] bg-segnale transition-[width] duration-300"
        style={{ width: `calc((100% - 28px) * ${quota.toFixed(4)})` }}
      />
      {tappe.map((t, i) => {
        const lit = acceso !== null && i <= acceso;
        const qui = acceso === i;
        const data = n <= DATE_SOTTO_TUTTE || i === 0 || i === n - 1;
        /* La scheda si appoggia a sinistra nel primo terzo, a destra
           nell'ultimo, al centro in mezzo: centrata su un rombo vicino al
           bordo uscirebbe dal riquadro. */
        const lato = i < n / 3 ? "left-0" : i >= (2 * n) / 3 ? "right-0" : "left-1/2 -translate-x-1/2";
        return (
          <li key={t.numero} className="relative z-[1] flex w-7 justify-center">
            <Link
              href={t.href}
              aria-label={`Tappa ${t.numero}: ${t.data}. ${t.titolo}`}
              onMouseEnter={() => setAcceso(i)}
              onFocus={() => setAcceso(i)}
              onBlur={() => setAcceso(null)}
              className="flex flex-col items-center gap-2 no-underline outline-none [&:focus-visible>i]:outline-2 [&:focus-visible>i]:outline-offset-4 [&:focus-visible>i]:outline-segnale"
            >
              <i
                aria-hidden
                className={`mt-[7px] block size-3.5 rotate-45 border-[1.5px] border-solid transition-transform duration-200 ${
                  qui ? "scale-[1.45]" : ""
                } ${
                  lit || (t.ultima && acceso === null) ? "border-segnale bg-segnale" : "border-mare bg-bg"
                }`}
              />
              <span
                aria-hidden
                className={`font-mono text-[9px] tracking-[0.06em] whitespace-nowrap ${qui ? "text-segnale" : "text-sommesso"} ${
                  data ? "" : "invisible"
                } ${i !== 0 && i !== n - 1 ? "max-[900px]:invisible" : ""}`}
              >
                {t.breve}
              </span>
              <span
                aria-hidden
                className={`pointer-events-none absolute bottom-[calc(100%+10px)] z-[2] w-[190px] border border-t-[3px] border-ink border-t-segnale bg-surface px-3 py-2.5 text-left font-serif text-[13px] leading-[1.4] text-ink shadow-[0_8px_24px_rgba(0,0,0,0.12)] transition-opacity duration-200 min-[901px]:w-[230px] ${lato} ${
                  qui ? "opacity-100" : "opacity-0"
                }`}
              >
                <b className="mb-1 block font-mono text-[10.5px] font-semibold tracking-[0.08em] text-segnale uppercase">{t.data}</b>
                {t.titolo}
                <em className="mt-1.5 block font-sans text-meta font-semibold not-italic text-mare">
                  Apri <span aria-hidden>↗</span>
                </em>
              </span>
            </Link>
          </li>
        );
      })}
    </ol>
  );
}
